import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const POLL_INTERVAL_MS = 30000;
const MAX_NOTIFICATION_COUNT = 99;

export default function NotificationBell() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);

  const role = user?.role;
  const showApprovals = ["manager", "admin"].includes(role);
  const showSecretariat = ["secretary", "dean", "admin"].includes(role);
  const shouldPoll = !!user && (showApprovals || showSecretariat);

  useEffect(() => {
    if (!shouldPoll) return undefined;
    let mounted = true;
    const fetchCounts = () => api.get("/notifications/counts")
      .then(r => { if (mounted) setCounts(r.data); })
      .catch(() => {});
    fetchCounts();
    const id = setInterval(fetchCounts, POLL_INTERVAL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, [shouldPoll]);

  if (!shouldPoll) return null;

  const total = counts?.total ?? 0;
  const pendingApprovals = (counts?.pending_approvals_vacation ?? 0) + (counts?.pending_approvals_wfh ?? 0);
  const approvedAwaitingPdf = (counts?.approved_awaiting_pdf_vacation ?? 0) + (counts?.approved_awaiting_pdf_wfh ?? 0);
  const decidedAwaitingSend = (counts?.decided_awaiting_send_vacation ?? 0) + (counts?.decided_awaiting_send_wfh ?? 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded hover:bg-secondary transition-colors"
          aria-label="Obavijesti"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-foreground" />
          {total > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
              data-testid="notification-badge"
            >
              {total > MAX_NOTIFICATION_COUNT ? `${MAX_NOTIFICATION_COUNT}+` : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b">
          <div className="font-medium text-sm">Obavijesti</div>
          <div className="text-xs text-muted-foreground">
            {total === 0 ? "Sve je obrađeno" : `${total} stavki čeka akciju`}
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {showApprovals && pendingApprovals > 0 && (
            <Link to="/odobrenja" className="block p-3 hover:bg-secondary/60 border-b" data-testid="notif-approvals">
              <div className="text-sm font-medium">Zahtjevi za suglasnost</div>
              <div className="text-xs text-muted-foreground mt-1">
                {counts.pending_approvals_vacation > 0 && <span>{counts.pending_approvals_vacation} godišnji odmor</span>}
                {counts.pending_approvals_vacation > 0 && counts.pending_approvals_wfh > 0 && <span> · </span>}
                {counts.pending_approvals_wfh > 0 && <span>{counts.pending_approvals_wfh} rad od kuće</span>}
              </div>
            </Link>
          )}
          {showSecretariat && approvedAwaitingPdf > 0 && (
            <Link to="/dekanat" className="block p-3 hover:bg-secondary/60 border-b" data-testid="notif-awaiting-pdf">
              <div className="text-sm font-medium">Za izdavanje rješenja</div>
              <div className="text-xs text-muted-foreground mt-1">
                {counts.approved_awaiting_pdf_vacation > 0 && <span>{counts.approved_awaiting_pdf_vacation} GO</span>}
                {counts.approved_awaiting_pdf_vacation > 0 && counts.approved_awaiting_pdf_wfh > 0 && <span> · </span>}
                {counts.approved_awaiting_pdf_wfh > 0 && <span>{counts.approved_awaiting_pdf_wfh} WFH</span>}
              </div>
            </Link>
          )}
          {showSecretariat && decidedAwaitingSend > 0 && (
            <Link to="/dekanat" className="block p-3 hover:bg-secondary/60" data-testid="notif-awaiting-send">
              <div className="text-sm font-medium">Rješenja za slanje</div>
              <div className="text-xs text-muted-foreground mt-1">
                {counts.decided_awaiting_send_vacation > 0 && <span>{counts.decided_awaiting_send_vacation} GO</span>}
                {counts.decided_awaiting_send_vacation > 0 && counts.decided_awaiting_send_wfh > 0 && <span> · </span>}
                {counts.decided_awaiting_send_wfh > 0 && <span>{counts.decided_awaiting_send_wfh} WFH</span>}
              </div>
            </Link>
          )}
          {total === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Trenutno nema novih zadataka.
            </div>
          )}
        </div>
        <div className="p-2 text-[10px] text-center text-muted-foreground border-t">
          Osvježava se svakih 30 sekundi
        </div>
      </PopoverContent>
    </Popover>
  );
}
